import {
    AUTHORIZATION_TOKEN_EVENTS,
    GOOGLE_PHOTOS_EXPORT_EVENTS
} from 'Sp/Angular/Events/Client/Gallery';
import { SESSION_STORAGE_EMAIL_KEY } from 'ts/common/constants/auth';

const emittedGooglePhotosExportGetForExportIds = new Set();

async function isMissingClientEmailError(response) {
    if (response.status !== 400) {
        return false;
    }

    try {
        const body = await response.clone().json();

        return body?.errorCode === 'clientEmailRequired';
    } catch (error) {
        return false;
    }
}

function emitGooglePhotosExportGetIfFirstForExport($rootScope, exportId, authorizationToken) {
    if (emittedGooglePhotosExportGetForExportIds.has(exportId)) {
        return;
    }

    emittedGooglePhotosExportGetForExportIds.add(exportId);

    $rootScope.$emit(GOOGLE_PHOTOS_EXPORT_EVENTS.GET, {
        id: exportId,
        authorizationToken
    });
}

export default [
    '$rootScope',
    '$scope',
    '$routeParams',
    'spAppData',
    'spAuthorizationToken',
    'spToast',
    'translateFilter',
    async function GooglePhotosExportController(
        $rootScope,
        $scope,
        $routeParams,
        spAppData,
        spAuthorizationToken,
        spToast,
        translateFilter
    ) {
        const brandData = spAppData.get('brandData');
        $scope.brandName = brandData.name;

        const searchParams = new URLSearchParams(location.search);
        const authKey = searchParams.get('authKey');

        if (!authKey) {
            spToast.toast({
                title: translateFilter('downloadGoogleUnexpectedError'),
                status: 'error',
                isClosable: true
            });

            return;
        }

        let exportId = Number(searchParams.get('exportId'));

        const email = sessionStorage.getItem(SESSION_STORAGE_EMAIL_KEY);
        const googleAuthorizedEmail = searchParams.get('google_authorized_email');

        if (!exportId) {
            let googlePhotosExportData;
            let googlePhotosExportProperties;

            try {
                googlePhotosExportProperties = JSON.parse(localStorage.getItem(authKey));
            } catch (error) {
                spToast.toast({
                    title: translateFilter('downloadGoogleUnexpectedError'),
                    status: 'error',
                    isClosable: true
                });

                return;
            }

            const { apiRoot } = spAppData.get('urls');

            let authorizationToken = await spAuthorizationToken.getAuthorizationTokenForEvent(
                false
            );

            try {
                const eventId = $rootScope.eventData.publicId
                    ? $rootScope.eventData.publicId
                    : $rootScope.eventData.id;
                const url = `${apiRoot}/brand/${$rootScope.brandData.id}/event/${eventId}/google-photos-export`;

                const buildOptions = (token) => ({
                    method: 'POST',
                    body: JSON.stringify(googlePhotosExportProperties),
                    headers: {
                        'Content-Type': 'application/vnd.shootproof+json',
                        Authorization: `Bearer ${token}`
                    }
                });

                let response = await fetch(url, buildOptions(authorizationToken));
                const isMissingEmail = await isMissingClientEmailError(response);

                if (response.status === 403 || isMissingEmail) {
                    // Either the cached token expired (tokens last 24 h), or it was minted
                    // before the client's email was known (no upfront email gate on this
                    // gallery). Refresh it, preserving the Google connection if the user
                    // already signed in this session.
                    authorizationToken = await spAuthorizationToken.getAuthorizationTokenForEvent(
                        true,
                        {
                            email,
                            google_authorized_email: $rootScope.googleEmail,
                            google_photos_export_id: $rootScope.googlePhotosExportId
                        }
                    );
                    response = await fetch(url, buildOptions(authorizationToken));
                }

                if (!response.ok) {
                    spToast.toast({
                        title: translateFilter('downloadGoogleUnexpectedError'),
                        status: 'error',
                        isClosable: true
                    });

                    return;
                }

                googlePhotosExportData = await response.json();
                exportId = googlePhotosExportData.exportId;
            } catch (error) {
                spToast.toast({
                    title: translateFilter('downloadGoogleUnexpectedError'),
                    status: 'error',
                    isClosable: true
                });

                return;
            }

            // Update URL search params with the export ID, so the URL points to the export:
            const newSearchParams = new URLSearchParams(searchParams);
            newSearchParams.set('exportId', exportId);
            history.replaceState(null, '', `${location.pathname}?${newSearchParams.toString()}`);

            if (googlePhotosExportData.googleEmail) {
                newSearchParams.set('google_authorized_email', googlePhotosExportData.googleEmail);
                history.replaceState(
                    null,
                    '',
                    `${location.pathname}?${newSearchParams.toString()}`
                );

                // Update the client access token with the new export ID so subsequent
                // exports reuse this Google connection without re-authenticating:
                authorizationToken = await spAuthorizationToken.getAuthorizationTokenForEvent(
                    true,
                    {
                        email,
                        google_authorized_email: googlePhotosExportData.googleEmail,
                        google_photos_export_id: exportId
                    }
                );

                // Hand the updated token back to the original gallery window via localStorage,
                // the same mechanism used by the OAuth return branch:
                localStorage.setItem(authKey, authorizationToken);

                $rootScope.isSignedIntoGoogle = true;
                $rootScope.googleEmail = googlePhotosExportData.googleEmail;
                $rootScope.googlePhotosExportId = exportId;

                $rootScope.$broadcast(
                    AUTHORIZATION_TOKEN_EVENTS.CREATED_EVENT_TOKEN,
                    authorizationToken
                );

                // Start polling for the export status, if not already started:
                emitGooglePhotosExportGetIfFirstForExport($rootScope, exportId, authorizationToken);
            } else {
                // Construct a URL to help us redirect to the Google Photos OAuth sign-in page:
                const redirectUrl = location.href.split('?')[0];

                const stateQueryParam = encodeURIComponent(
                    JSON.stringify({ authKey, exportId, redirectUrl })
                );

                const emailQueryPart = email ? `&email=${email}` : '';
                const googleOAuthUrl = `${apiRoot}/oauth/google?state=${stateQueryParam}${emailQueryPart}`;

                location.href = googleOAuthUrl;
            }
        } else if (googleAuthorizedEmail) {
            // We're being redirected from the Google Photos OAuth sign-in page, so we need to update the client access token and start polling for the export status:
            const authorizationToken = await spAuthorizationToken.getAuthorizationTokenForEvent(
                true,
                {
                    email,
                    google_authorized_email: googleAuthorizedEmail,
                    google_photos_export_id: exportId
                }
            );

            // Temporarily set the export key in local storage, to authorize the original tab for Google sign-in:
            localStorage.setItem(authKey, authorizationToken);

            $rootScope.isSignedIntoGoogle = true;
            $rootScope.googleEmail = googleAuthorizedEmail;
            $rootScope.googlePhotosExportId = exportId;

            $rootScope.$broadcast(
                AUTHORIZATION_TOKEN_EVENTS.CREATED_EVENT_TOKEN,
                authorizationToken
            );

            // Start polling for the export status, if not already started:
            emitGooglePhotosExportGetIfFirstForExport($rootScope, exportId, authorizationToken);
        }

        $rootScope.$on(GOOGLE_PHOTOS_EXPORT_EVENTS.GOT, (_, response) => {
            if (exportId === response.exportId) {
                $rootScope.$evalAsync(() => {
                    $scope.googlePhotosExport = response;
                });
            }
        });
    }
];
